import { InfiniteData, useMutation, useQueryClient } from "@tanstack/react-query";
import { chatClient } from "@core/clients";
import { OrderChatAttachment, OrderChatMessage, OrderChatMessageType, OrderChatMessagesPage } from "@core/types";
import { orderChatQueryKeys } from "./keys";
import { mergeNewMessageIntoCache, removeMessageFromCache } from "./utils";

type Variables = {
  chatId: number;
  file: File;
};

type Context = {
  tempMessageId: number;
  previewUrl: string;
};

const DEFAULT_CONTENT_TYPE = "application/octet-stream";

const pickMessageType = (mime: string): OrderChatMessageType => {
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  return "file";
};

const buildOptimisticMessage = (chatId: number, file: File, tempId: number, previewUrl: string): OrderChatMessage => {
  const mimeType = file.type || DEFAULT_CONTENT_TYPE;
  const now = new Date().toISOString();
  const attachment: OrderChatAttachment = {
    id: tempId,
    chatId,
    messageId: tempId,
    bucketId: "order-chat-media",
    storagePath: "",
    fileName: file.name,
    mimeType,
    fileSize: file.size,
    status: "pending",
    createdAt: now,
    signedUrl: previewUrl,
  };

  return {
    id: tempId,
    chatId,
    senderType: "customer",
    senderCustomerId: null,
    senderAdminUserId: null,
    messageType: pickMessageType(mimeType),
    text: null,
    createdAt: now,
    deletedAt: null,
    attachments: [attachment],
    status: "sending",
  };
};

const useSendOrderChatFileMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<OrderChatMessage, unknown, Variables, Context>({
    mutationKey: ["chat", "send-file"],
    mutationFn: async ({ chatId, file }) => {
      const intent = await chatClient.createAttachmentIntent({ chatId, file });
      await chatClient.uploadAttachmentFile(intent, file);
      return chatClient.confirmAttachment({ chatId, attachmentId: intent.attachmentId });
    },
    onMutate: async ({ chatId, file }) => {
      await queryClient.cancelQueries(orderChatQueryKeys.messages(chatId));

      const tempMessageId = -Date.now();
      const previewUrl = URL.createObjectURL(file);
      const optimistic = buildOptimisticMessage(chatId, file, tempMessageId, previewUrl);

      queryClient.setQueryData<InfiniteData<OrderChatMessagesPage>>(orderChatQueryKeys.messages(chatId), (cache) =>
        mergeNewMessageIntoCache(cache, optimistic),
      );

      return { tempMessageId, previewUrl };
    },
    onSuccess: (message, variables, context) => {
      queryClient.setQueryData<InfiniteData<OrderChatMessagesPage>>(
        orderChatQueryKeys.messages(variables.chatId),
        (cache) => {
          const withoutTemp = context ? removeMessageFromCache(cache, context.tempMessageId) : cache;
          return mergeNewMessageIntoCache(withoutTemp, message);
        },
      );
      if (context?.previewUrl) {
        window.setTimeout(() => URL.revokeObjectURL(context.previewUrl), 1500);
      }
    },
    onError: (_error, variables, context) => {
      if (!context) return;
      URL.revokeObjectURL(context.previewUrl);
      queryClient.setQueryData<InfiniteData<OrderChatMessagesPage>>(
        orderChatQueryKeys.messages(variables.chatId),
        (cache) => removeMessageFromCache(cache, context.tempMessageId),
      );
    },
  });
};

export default useSendOrderChatFileMutation;
